import { ADD_TALENT_PROFESSION, ADD_TALENT_PROFESSION_PRO } from '@gql/talentProfessions';
import { useAuth } from '@contexts/AuthContext';
import { FormControl, FormHelperText, FormLabel as MuiFormLabel, styled } from '@mui/material';
import * as React from 'react';
import { Controller } from 'react-hook-form';
import { AutoCompleteField } from './AutoCompleteField';
import { AutoCompleteSingleField } from './AutoCompleteSingleField';
import { NestedSubOption, Props } from './types';
import { flatten } from './utils';

export type { NestedOption, NestedSubOption, Props } from './types';

const FormLabel = styled(MuiFormLabel)(() => ({
    marginBottom: '4px',
}));

export function AutoCompleteTalentProfessions({
    control,
    name,
    label,
    placeholder,
    disabled,
    options,
    groupedOptions,
    defaultValue,
    multiple = true,
    ...props
}: Props & { multiple?: boolean }) {
    const { isUpercut } = useAuth();
    const mutation = isUpercut ? ADD_TALENT_PROFESSION : ADD_TALENT_PROFESSION_PRO;

    const _options = React.useMemo(() => {
        return (groupedOptions ? flatten(groupedOptions) : options) || [];
    }, [groupedOptions, options]);

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={multiple ? defaultValue || [] : defaultValue || null}
            rules={props.rules}
            render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                <FormControl sx={{ flex: 1, minWidth: 200 }} disabled={disabled} fullWidth error={!!error}>
                    <FormLabel htmlFor={name}>{label}</FormLabel>
                    {multiple ? (
                        <AutoCompleteField
                            ref={ref}
                            viewOnly={props.viewOnly}
                            options={_options}
                            value={value}
                            onChange={val => {
                                onChange(val);
                                props.onChange?.(val);
                            }}
                            label={label}
                            mutation={mutation}
                            placeholder={placeholder}
                            disabled={disabled}
                            allowAdd={props.allowAdd}
                            multiple={multiple}
                            groupBy={groupedOptions ? (option: NestedSubOption) => option?.parent?.name : undefined}
                            error={!!error}
                        />
                    ) : (
                        <AutoCompleteSingleField
                            ref={ref}
                            viewOnly={props.viewOnly}
                            options={_options}
                            value={value}
                            onChange={val => {
                                onChange(val);
                                props.onChange?.(val);
                            }}
                            label={label}
                            mutation={mutation}
                            refetchQueries={props.refetchQueries}
                            placeholder={placeholder}
                            disabled={disabled}
                            allowAdd={props.allowAdd}
                            groupBy={groupedOptions ? (option: NestedSubOption) => option?.parent?.name : undefined}
                            error={!!error}
                        />
                    )}

                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                </FormControl>
            )}
        />
    );
}
